import React, { useState } from 'react';
import { Outlet, NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Plane, Calendar, Users, BookOpen,
  MapPin, Menu, X, Bell, BarChart3, LogOut,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

const nav = [
  { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/reporting', icon: BarChart3, label: 'Reporting' },
  { to: '/flights', icon: Plane, label: 'Flights' },
  { to: '/events', icon: Calendar, label: 'Events' },
  { to: '/users', icon: Users, label: 'Users' },
  { to: '/bookings', icon: BookOpen, label: 'Bookings' },
  { to: '/destinations', icon: MapPin, label: 'Destinations' },
];

export default function Layout() {
  const [open, setOpen] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const current = nav.find(n => location.pathname.startsWith(n.to));

  // First letter of email for avatar
  const initial = user?.email?.[0]?.toUpperCase() ?? 'A';

  const handleLogout = async () => {
    await logout();
    toast.success('Signed out successfully.');
    navigate('/login', { replace: true });
  };

  return (
    <div className="flex h-screen overflow-hidden bg-[#080E1E]">
      {/* Sidebar */}
      <aside
        className={`${open ? 'w-64' : 'w-16'} flex-shrink-0 bg-[#111827] border-r border-[#1E2D45]
          flex flex-col transition-all duration-300 z-20`}
      >
        {/* Logo */}
        <div className="flex items-center gap-3 px-4 py-5 border-b border-[#1E2D45]">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0">
            <img src="/metamax-logo.png" alt="MetaMax" className="w-8 h-8 object-contain" />
          </div>
          {open && (
            <div>
              <div className="text-white font-bold text-sm tracking-widest">META MAX</div>
              <div className="text-[#546E7A] text-xs">Admin Panel</div>
            </div>
          )}
        </div>

        {/* Nav */}
        <nav className="flex-1 py-4 overflow-y-auto">
          {nav.map(({ to, icon: Icon, label }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 mx-2 rounded-xl mb-1 transition-all
                ${isActive
                  ? 'bg-[#1A3C8F]/30 text-[#2D5BE3] border border-[#2D5BE3]/30'
                  : 'text-[#546E7A] hover:bg-white/5 hover:text-white'}`
              }
            >
              <Icon size={18} className="flex-shrink-0" />
              {open && <span className="text-sm font-medium">{label}</span>}
            </NavLink>
          ))}
        </nav>

        {/* User + logout */}
        {open ? (
          <div className="border-t border-[#1E2D45] p-4 space-y-3">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-[#2D5BE3] to-[#0D1F4F]
                flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
                {initial}
              </div>
              <div className="min-w-0">
                <div className="text-white text-xs font-medium truncate">{user?.email}</div>
                <div className="text-[#546E7A] text-xs">Administrator</div>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-2 px-3 py-2 rounded-xl
                text-[#546E7A] hover:bg-red-500/10 hover:text-red-400 transition-colors text-sm"
            >
              <LogOut size={15} />
              Sign out
            </button>
          </div>
        ) : (
          <div className="border-t border-[#1E2D45] flex flex-col items-center gap-3 py-4">
            <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-[#2D5BE3] to-[#0D1F4F]
              flex items-center justify-center text-white text-sm font-bold">
              {initial}
            </div>
            <button
              onClick={handleLogout}
              title="Sign out"
              className="text-[#546E7A] hover:text-red-400 transition-colors"
            >
              <LogOut size={16} />
            </button>
          </div>
        )}

        {/* Collapse toggle */}
        <button
          onClick={() => setOpen(!open)}
          className="flex items-center justify-center p-3 border-t border-[#1E2D45]
            text-[#546E7A] hover:text-white transition-colors"
        >
          {open ? <X size={18} /> : <Menu size={18} />}
        </button>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Topbar */}
        <header className="h-16 bg-[#111827] border-b border-[#1E2D45] flex items-center
          justify-between px-6 flex-shrink-0">
          <div>
            <h1 className="text-white font-semibold text-lg">
              {current?.label || 'Dashboard'}
            </h1>
            <p className="text-[#546E7A] text-xs">Meta Max Admin Panel</p>
          </div>
          <div className="flex items-center gap-3">
            <button className="w-9 h-9 rounded-xl bg-[#1A2235] border border-[#1E2D45]
              flex items-center justify-center text-[#546E7A] hover:text-white transition-colors">
              <Bell size={16} />
            </button>
            <button
              onClick={handleLogout}
              title="Sign out"
              className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#2D5BE3] to-[#0D1F4F]
                flex items-center justify-center text-white text-sm font-bold
                hover:opacity-80 transition-opacity"
            >
              {initial}
            </button>
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 overflow-y-auto p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
